import type { Graphics } from 'pixi.js';
import type { AlleyExtra } from '../../data/full/festival';
import { parseHexColor } from '../palette';
import { FOLK, PATH_COLORS } from './path-way';

export interface StandColors {
  readonly cloth: number;
  readonly trim: number;
}

const WOOD = parseHexColor('#c49a5c');
const DARK_WOOD = parseHexColor('#3a2418');
const POT = parseHexColor('#3a2030');
const CHAI = parseHexColor('#c86a3a');
const STEAM = parseHexColor('#fbeee0');
const GRIDDLE = parseHexColor('#2a2626');
const CREPE = parseHexColor('#e8b060');
const RINGS = ['#7cf2b0', '#5fd0c8', '#ff6fa8', '#f0b050'].map(parseHexColor);

export type ExtraDrawing = (
  g: Graphics,
  over: Graphics,
  colors: StandColors,
) => { x: number; y: number }[];

const folk = (index: number) => FOLK[index % FOLK.length] ?? PATH_COLORS.brass;

function person(g: Graphics, x: number, y: number, color: number, radius = 7): void {
  g.circle(x, y, radius).fill(color).stroke({ width: 2, color: PATH_COLORS.outline });
}

function cushion(g: Graphics, x: number, y: number, color: number): void {
  g.roundRect(x - 11, y - 8, 22, 16, 6)
    .fill(color)
    .stroke({ width: 2, color: PATH_COLORS.outline });
}

function steam(g: Graphics, x: number, y: number, rise: number): void {
  g.moveTo(x, y)
    .bezierCurveTo(x + 8, y - rise * 0.3, x - 8, y - rise * 0.6, x + 2, y - rise)
    .stroke({ width: 3, color: STEAM, alpha: 0.45, cap: 'round' });
}

const chai: ExtraDrawing = (g, over, { cloth, trim }) => {
  over.circle(0, 0, 78).fill(cloth).stroke({ width: 4, color: trim });
  for (let i = 0; i < 24; i++) {
    const angle = (i / 24) * Math.PI * 2;
    over.circle(Math.cos(angle) * 78, Math.sin(angle) * 78, 4).fill(trim);
  }
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    over.moveTo(0, 0).lineTo(Math.cos(angle) * 78, Math.sin(angle) * 78);
  }
  over.stroke({ width: 2, color: trim, alpha: 0.55 });
  over.circle(0, 0, 9).fill(PATH_COLORS.glow).stroke({ width: 2, color: PATH_COLORS.outline });
  const pot = { x: 40, y: 122 };
  g.circle(pot.x, pot.y, 17).fill(POT).stroke({ width: 3, color: PATH_COLORS.brass });
  g.circle(pot.x, pot.y, 11).fill(CHAI);
  steam(g, pot.x, pot.y - 4, 34);
  cushion(g, pot.x - 46, pot.y, folk(2));
  cushion(g, pot.x + 46, pot.y - 2, PATH_COLORS.brass);
  cushion(g, pot.x, pot.y + 36, trim);
  person(g, pot.x - 30, pot.y - 14, folk(0));
  person(g, pot.x + 38, pot.y - 22, folk(1));
  person(g, pot.x + 4, pot.y + 44, folk(2));
  return [pot, { x: 0, y: 0 }];
};

const bracelets: ExtraDrawing = (g, over, { cloth, trim }) => {
  over.rect(-112, -56, 224, 104).fill(cloth);
  for (const x of [-100, -56, -12, 32, 76]) {
    over.rect(x, -56, 20, 104).fill(trim);
  }
  over.roundRect(-112, -56, 224, 104, 8).stroke({ width: 3, color: PATH_COLORS.outline });
  g.roundRect(-100, 60, 200, 26, 6)
    .fill(PATH_COLORS.pole)
    .stroke({ width: 2, color: PATH_COLORS.brass });
  const rings = [];
  for (let i = 0; i < 6; i++) {
    const x = -70 + i * 28;
    g.circle(x, 73, 7).stroke({ width: 3.5, color: RINGS[i % RINGS.length] ?? trim });
    rings.push({ x, y: 73 });
  }
  person(g, 120, 96, folk(3));
  return rings;
};

const crepes: ExtraDrawing = (g, _over, { cloth, trim }) => {
  g.roundRect(-80, -50, 160, 84, 10).fill(cloth).stroke({ width: 3, color: PATH_COLORS.outline });
  for (let x = -70; x < 80; x += 20) {
    g.moveTo(x, 34)
      .arc(x + 10, 34, 10, Math.PI, 0, true)
      .fill(trim);
  }
  g.roundRect(-78, 44, 156, 30, 5).fill(DARK_WOOD).stroke({ width: 2, color: PATH_COLORS.outline });
  for (const x of [-40, 30]) {
    g.circle(x, 59, 13).fill(GRIDDLE).stroke({ width: 2, color: PATH_COLORS.outline });
    g.circle(x, 59, 9).fill(CREPE);
    steam(g, x, 46, 30);
  }
  for (const [x, y] of [
    [-112, 30],
    [-120, 70],
  ] as const) {
    g.circle(x, y, 12).fill(DARK_WOOD).stroke({ width: 2, color: PATH_COLORS.outline });
  }
  person(g, 100, 96, folk(0));
  person(g, 128, 74, folk(3));
  person(g, -60, 104, folk(1));
  return [
    { x: -40, y: 59 },
    { x: 30, y: 59 },
  ];
};

const thrift: ExtraDrawing = (g, _over, { cloth, trim }) => {
  g.roundRect(-90, -40, 180, 100, 6).fill(cloth).stroke({ width: 2, color: PATH_COLORS.outline });
  g.roundRect(-78, -28, 156, 76, 4).stroke({ width: 3, color: trim, alpha: 0.8 });
  g.moveTo(-100, -60).lineTo(100, -60).stroke({ width: 5, color: WOOD, cap: 'round' });
  for (const x of [-100, 100]) {
    g.circle(x, -60, 6).fill(DARK_WOOD).stroke({ width: 2, color: WOOD });
  }
  for (let i = 0; i < 9; i++) {
    const x = -84 + i * 21;
    const color = i % 3 === 0 ? trim : folk(i);
    g.poly([x - 9, -60, x - 6, -38, x + 6, -38, x + 9, -60])
      .fill(color)
      .stroke({ width: 1.5, color: PATH_COLORS.outline });
  }
  person(g, -30, 10, folk(4));
  person(g, 34, 22, folk(1));
  return [{ x: 0, y: 0 }];
};

export const ALLEY_DRAWINGS: Readonly<Record<AlleyExtra, ExtraDrawing>> = {
  chai,
  bracelets,
  crepes,
  thrift,
};
